/**
 * Monta payload do report OS v2 (Impressão 2) a partir da ordem do Board.
 */

import { getApiBaseUrl } from '../../../../utils/apiConfig';
import { getBoardClienteExibicao } from '../../utils/clienteBoardDisplay';
import { formatReincidenciaDataAbertura } from '../../utils/orderDetailsReincidenciaUtils';
import { getTipoOsLabelForPrint } from '../../../new-os/utils/osPrintPdf';

function pickFirstFilled(...values) {
  for (const value of values) {
    if (value == null) continue;
    const normalized = String(value).trim();
    if (normalized) return normalized;
  }
  return '';
}

/** Máscara CEP: 30830430 → 30830-430 */
function formatCepInText(text) {
  return String(text || '').replace(/\b(\d{5})-?(\d{3})\b/g, '$1-$2');
}

/** Máscara CPF (11) ou CNPJ (14). */
function formatCpfCnpj(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  const s = String(raw || '').trim();
  return s;
}

async function fetchClienteDoc(order) {
  const fromOrder = pickFirstFilled(
    order?.TB01008_CNPJ,
    order?.TB01008_CPF,
    order?.clienteCnpj,
    order?.cnpjCliente,
    order?.clienteDoc
  );
  if (fromOrder) return formatCpfCnpj(fromOrder);

  const numeroOS = pickFirstFilled(order?.id, order?.TB02115_CODIGO, order?.numeroOS);
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (!numeroOS || !token) return '';
  try {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/orders/${encodeURIComponent(numeroOS)}/cliente-doc`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) return '';
    const json = await res.json();
    return formatCpfCnpj(json?.data?.doc);
  } catch {
    return '';
  }
}

function dash(value, fallback = '—') {
  const s = value == null ? '' : String(value).trim();
  return s || fallback;
}

function formatContador(val) {
  if (val == null || val === '') return '—';
  if (typeof val === 'number' && Number.isFinite(val)) {
    return Math.trunc(val).toLocaleString('pt-BR');
  }
  const raw = String(val).trim();
  if (!raw) return '—';
  const normalized = raw.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  if (!Number.isFinite(n)) return raw;
  return Math.trunc(n).toLocaleString('pt-BR');
}

function formatDateOnlyBrFromString(value) {
  if (!value) return null;
  const s = String(value).trim();
  const slashMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slashMatch) return `${slashMatch[1]}/${slashMatch[2]}/${slashMatch[3]}`;
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  return s || null;
}

function getHistoricoFechamentoTimestamp(value) {
  if (!value) return Number.NEGATIVE_INFINITY;
  const s = String(value).trim();
  if (!s) return Number.NEGATIVE_INFINITY;
  const brMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (brMatch) {
    const [, dd, mm, yyyy, hh = '00', mi = '00', ss = '00'] = brMatch;
    const ts = Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi), Number(ss));
    return Number.isNaN(ts) ? Number.NEGATIVE_INFINITY : ts;
  }
  const parsed = Date.parse(s.replace(' ', 'T'));
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

function sortHistoricoByFechamentoDesc(list) {
  return [...list].sort((a, b) => {
    const tsA = getHistoricoFechamentoTimestamp(a && a.TB02122_DATAFIM);
    const tsB = getHistoricoFechamentoTimestamp(b && b.TB02122_DATAFIM);
    return tsB - tsA;
  });
}

function getPrevisaoLabel(order) {
  if (order?.previsaoLabelOverride != null && String(order.previsaoLabelOverride).trim() !== '') {
    return String(order.previsaoLabelOverride).trim();
  }
  const forecastGroup = order?.forecastGroup;
  if (!forecastGroup || forecastGroup === 'Em Aberto' || forecastGroup === 'Em aberto') {
    return 'Sem previsão';
  }
  if (forecastGroup === 'Previsto para hoje') {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${today.getFullYear()}`;
  }
  if (forecastGroup === 'Previstas para amanhã') {
    const today = new Date();
    const currentDay = today.getDay();
    let forecastDate;
    if (currentDay >= 1 && currentDay <= 4) {
      forecastDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    } else {
      const daysUntilMonday = currentDay === 0 ? 1 : 8 - currentDay;
      forecastDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + daysUntilMonday);
    }
    const dd = String(forecastDate.getDate()).padStart(2, '0');
    const mm = String(forecastDate.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${forecastDate.getFullYear()}`;
  }
  if (forecastGroup === 'FUTURA' && order.groupForecastDate) {
    return formatDateOnlyBrFromString(String(order.groupForecastDate)) || 'Sem previsão';
  }
  if (order?.previsao) {
    return formatDateOnlyBrFromString(order.previsao) || 'Sem previsão';
  }
  return 'Sem previsão';
}

function buildLocalizacao(order) {
  const localEquip = pickFirstFilled(order?.TB02112_LOCAL);
  const localOs = pickFirstFilled(order?.TB02115_LOCAL);
  const site = pickFirstFilled(order?.site_nome);
  const depto = pickFirstFilled(order?.depto_nome);
  const parts = [];
  if (localEquip) parts.push(localEquip);
  else if (localOs) parts.push(localOs);
  if (site) parts.push(site);
  if (depto) parts.push(depto);
  return parts.join(' · ');
}

function buildIdentificacao(order) {
  const serie = pickFirstFilled(order?.numeroSerie, order?.TB02115_NUMSERIE, order?.serie, order?.numSerie);
  const patrimonio = pickFirstFilled(order?.patrimonio, order?.TB02112_PAT);
  if (serie && patrimonio) return `${serie} · ${patrimonio}`;
  return serie || patrimonio || '';
}

function formatAbertura(order) {
  const raw = pickFirstFilled(order?.dataAbertura, order?.TB02115_DATA, order?.abertura);
  if (!raw) return '';
  const formatted = formatReincidenciaDataAbertura(raw);
  return formatted === '—' ? '' : formatted;
}

function condicaoTone(condicao) {
  const s = String(condicao || '').trim();
  if (s.startsWith('01')) return 'ok';
  return 'warn';
}

function mapHistoricoRow(row) {
  const condicao = dash(row?.TB01055_NOME, '');
  return {
    osNum: dash(row?.TB02115_CODIGO),
    dataOcorrencia: dash(row?.TB02115_DATA),
    defeito: dash(row?.TB02115_NOME),
    tecnico: dash(row?.TB01024_NOME),
    fechamento: dash(row?.TB02122_DATAFIM),
    pb: formatContador(row?.TB02122_CONTADOR),
    cor: formatContador(row?.TB02122_CONTADORC),
    condicao: condicao || '—',
    condicaoTone: condicaoTone(condicao),
    laudo: dash(row?.TB02122_OBS, '—'),
  };
}

async function fetchHistoricoRows(order) {
  const serie = pickFirstFilled(order?.numeroSerie, order?.TB02115_NUMSERIE, order?.serie, order?.numSerie);
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (!serie || !token) return [];
  try {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/oficina/ordens-serie?serie=${encodeURIComponent(serie)}`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) return [];
    const data = await res.json();
    const list = data && data.success && Array.isArray(data.data) ? data.data : [];
    return sortHistoricoByFechamentoDesc(list).slice(0, 6).map(mapHistoricoRow);
  } catch {
    return [];
  }
}

/**
 * @param {object} order
 * @param {{ includeHistory?: boolean }} [options]
 * @returns {Promise<object>}
 */
export async function buildOsReportV2Payload(order, options = {}) {
  const includeHistory = options.includeHistory !== false;
  if (!order) {
    return {
      empresaNome: '—',
      empCnpj: '',
      empEndereco: '',
      empEmail: '',
      osNumber: '—',
      tipoLabel: 'CORRETIVA',
      statusLabel: 'Em aberto',
      cliente: '—',
      clienteDoc: '',
      telefone: '',
      endereco: '—',
      previsao: 'Sem previsão',
      localizacao: '',
      equipamento: '—',
      numeroSerie: '',
      patrimonio: '',
      identificacao: '',
      defeito: '—',
      observacao: '',
      observacaoGeral: '',
      abertura: '',
      companyLogoUrl: '',
      solicitante: '',
      history: [],
    };
  }

  const empresaNome = pickFirstFilled(order.TB01007_NOME, order.emp_nome, order.empresa) || 'NOME DA EMPRESA';
  const empCnpj = formatCpfCnpj(pickFirstFilled(order.TB01007_CNPJ, order.emp_cnpj, order.cnpj));
  const empEndereco = formatCepInText(pickFirstFilled(order.TB01007_ENDERECO, order.emp_endereco));
  const empEmail = pickFirstFilled(order.TB01007_EMAIL, order.emp_email);

  let endereco = pickFirstFilled(order.endereco);
  const localExtra = pickFirstFilled(order.TB02112_LOCAL);
  if (localExtra && endereco && !endereco.includes(localExtra)) {
    endereco = `${endereco} - ${localExtra}`;
  } else if (!endereco && localExtra) {
    endereco = localExtra;
  }
  endereco = formatCepInText(endereco);

  const solicitante = pickFirstFilled(
    order.TB02115_SOLICITANTE,
    order.solicitante
  );
  const telefone = pickFirstFilled(
    order.telefone,
    order.TB02115_FONE,
    order.TB01008_FONE,
    order.contato
  );
  const [clienteDoc, history] = await Promise.all([
    fetchClienteDoc(order),
    includeHistory ? fetchHistoricoRows(order) : Promise.resolve([]),
  ]);
  const numeroSerie = pickFirstFilled(order.numeroSerie, order.TB02115_NUMSERIE, order.serie, order.numSerie);
  const patrimonio = pickFirstFilled(order.patrimonio, order.TB02112_PAT);
  const equipamento = pickFirstFilled(order.equipamento, order.TB01010_NOME, order.TB01010_RESUMIDO);
  const defeito = pickFirstFilled(order.defeitoNome, order.motivoOS);
  const observacao = pickFirstFilled(order.defeitoObservacao, order.motivoOSObservacao);
  const observacaoGeral = pickFirstFilled(
    order.observacaoGeral,
    order.obsGeralText,
    order.TB02115_OBS
  );

  let companyLogoUrl = '';
  try {
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (token) {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/config/company`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        companyLogoUrl = pickFirstFilled(json?.data?.logoViewUrl);
      }
    }
  } catch {
    companyLogoUrl = '';
  }

  return {
    empresaNome,
    empCnpj,
    empEndereco,
    empEmail,
    companyLogoUrl,
    osNumber: order.id != null ? String(order.id) : '000000',
    tipoLabel: getTipoOsLabelForPrint(order.tipo),
    statusLabel: pickFirstFilled(order.statusLabel, order.status, getTipoOsLabelForPrint(order.tipo)),
    cliente: getBoardClienteExibicao(order) || '—',
    clienteDoc,
    telefone,
    endereco: endereco || '—',
    previsao: getPrevisaoLabel(order),
    localizacao: buildLocalizacao(order),
    equipamento: equipamento || '—',
    numeroSerie: numeroSerie || '',
    patrimonio: patrimonio || '',
    identificacao: buildIdentificacao(order),
    defeito: defeito || '—',
    observacao,
    observacaoGeral,
    abertura: formatAbertura(order),
    solicitante,
    history,
    closure: order.reportClosure || null,
  };
}
